"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import ConfirmationDialog from "../components/ConfirmationDialog";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProductMaterialManagement = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Last 7 Days");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [newMaterial, setNewMaterial] = useState({ name: "" });
  const [editMaterial, setEditMaterial] = useState({ id: "", name: "" });
  const [materialToDelete, setMaterialToDelete] = useState(null);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        "https://logicx.premierlam.com/api/pixel-get-product-material",
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
          },
        }
      );

      setMaterials(response.data.data || []);
    } catch (err) {
      const msg = err.response?.data?.message;

      // ✅ Handle expired token here
      if (msg === "Invalid or expired token") {
        localStorage.removeItem("accessToken");
        window.location.href = "/login";
        return;
      }
      setError("Failed to fetch materials");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMaterial = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await axios.post(
        "https://logicx.premierlam.com/api/pixel-add-product-material",
        { product_material: newMaterial.name },
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
          },
        }
      );
      await fetchMaterials();
      setIsAddDialogOpen(false);
      setNewMaterial({ name: "" });
      toast.success("Material added successfully!");
    } catch(err) {
      toast.error(err.response?.data?.message || "Failed to add material");
      setError("Failed to add material");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditMaterial = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await axios.put(
        `https://logicx.premierlam.com/api/pixel-edit-product-material/${editMaterial.id}`,
        { product_material: editMaterial.name },
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
          },
        }
      );
      await fetchMaterials();
      setIsEditDialogOpen(false);
      toast.success("Material updated successfully!");
    } catch(err) {
      toast.error(err.response?.data?.message || "Failed to update material");

      setError("Failed to update material");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsSubmitting(true);
      await axios.delete(
        `https://logicx.premierlam.com/api/pixel-delete-product-material/${materialToDelete.id}`,
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
          },
        }
      );
      await fetchMaterials();
      setIsDeleteDialogOpen(false);
      setMaterialToDelete(null);
      toast.success("Material deleted successfully!");
    } catch(err) {
      toast.error(err.response?.data?.message || "Failed to delete material");

      setError("Failed to delete material");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredMaterials = materials.filter((m) =>
    m.product_material.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="min-h-screen bg-gray-50 p-2">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                Product Materials
              </h1>
              <p className="text-gray-500">Manage your product materials</p>
            </div>
            <button
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Material
            </button>
          </div>

          {/* ✅ Unified Search + Table Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            {/* Search + Filter Row */}
            <div className="flex justify-between items-center mb-6">
              {/* Search */}
              <div className="relative">
                <svg
                  className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none w-64"
                />
              </div>

              {/* Filters */}
              {/* <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Status</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option>All</option>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Sort By</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option>Last 7 Days</option>
                  <option>Last 30 Days</option>
                  <option>Last 90 Days</option>
                </select>
              </div>
            </div> */}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 w-12">
                        <input
                          type="checkbox"
                          checked={selectedItems.length === materials.length}
                          onChange={(e) =>
                            e.target.checked
                              ? setSelectedItems(materials.map((m) => m.id))
                              : setSelectedItems([])
                          }
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                        Material Name
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                        Created On
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                        Added By
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMaterials.map((material) => (
                      <tr
                        key={material.id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-4 px-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(material.id)}
                            onChange={() =>
                              setSelectedItems((prev) =>
                                prev.includes(material.id)
                                  ? prev.filter((id) => id !== material.id)
                                  : [...prev, material.id]
                              )
                            }
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-900">
                          {material.product_material}
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">
                          {formatDate(material.created_at)}
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">
                          {material.added_by}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setEditMaterial({
                                  id: material.id,
                                  name: material.product_material,
                                });
                                setIsEditDialogOpen(true);
                              }}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setMaterialToDelete(material);
                                setIsDeleteDialogOpen(true);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Edit Dialog */}
          {isEditDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white p-6 rounded-lg w-full max-w-md relative">
                <button
                  className="absolute top-2 right-3 text-gray-400 hover:text-gray-700"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  ✖
                </button>
                <h2 className="text-lg font-semibold mb-4">Edit Material</h2>
                <form onSubmit={handleEditMaterial} className="space-y-3">
                  <input
                    type="text"
                    value={editMaterial.name}
                    onChange={(e) =>
                      setEditMaterial({ ...editMaterial, name: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                    placeholder="Enter material name"
                    required
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setIsEditDialogOpen(false)}
                      className="px-3 py-2 border rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-3 py-2 bg-orange-500 text-white rounded-md"
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          {/* ADD DIALOG */}
          {isAddDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
                <button
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  ✖
                </button>

                <h2 className="text-lg font-semibold mb-4">Add New Material</h2>

                <form onSubmit={handleAddMaterial} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Material Name</label>
                    <input
                      type="text"
                      value={newMaterial.name}
                      onChange={(e) => setNewMaterial({ name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      placeholder="Enter material name"
                      required
                    />
                  </div>

                  {error && (
                    <p className="text-red-600 text-sm">{error}</p>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setIsAddDialogOpen(false)}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                    >
                      {isSubmitting ? "Adding..." : "Add Material"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          <ConfirmationDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onConfirm={handleDeleteConfirm}
            title="Delete Material"
            message={`Are you sure you want to delete "${materialToDelete?.product_material}"?`}
            confirmText="Delete"
            type="danger"
          />
        </div>
      </div>
    </>
  );
};

export default ProductMaterialManagement;

